import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { newSignUpPaylod } from '@edtech-ui/core/models/interfaces/new-singup-flow/new-otp-mobile';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { OneSignalService } from '@edtech-ui/core/services/one-signal-service/one-signal.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { passwordStrengthValidator, passwordCrossFieldValidator } from '@edtech-ui/core/utils/password-validators';
import { COURSES_LIST, getPassingYears } from '@edtech-ui/core/constants/education-master-data';
import { CollegeItem } from '@edtech-ui/core/services/institution.service';
import { CantFindCollegeModalComponent } from '../../shared/cant-find-college-modal/cant-find-college-modal.component';
import { CollegeAutocompleteComponent } from '../../shared/college-autocomplete/college-autocomplete.component';

@Component({
	selector: 'app-user-registration-mobile-otp',
	standalone: true,
	imports: [CommonModule, MatProgressSpinnerModule, MatDialogModule, FormsModule, ReactiveFormsModule, RouterModule, CollegeAutocompleteComponent],
	templateUrl: './user-registration-mobile-otp.component.html',
	styleUrl: './user-registration-mobile-otp.component.scss'
})
export class UserRegistrationMobileOtpComponent implements OnInit, OnDestroy {
	signUpForm!: FormGroup;
	isLoading = false;
	phone_number!: string;
	subscription = new Subscription();
	showPassword = false;
	showConfirmPassword = false;
	redirectUrl = '';

	coursesList = COURSES_LIST;
	passingYears = getPassingYears();

	selectedInstitutionId: string | null = null;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private _toastr: ToastrService,
		private _authService: AuthServiceService,
		private _oneSignalService: OneSignalService,
		private _dialog: MatDialog
	) {
		this.signUpForm = new FormGroup({
			first_name: new FormControl('', [
				Validators.required,
				Validators.minLength(2),
				Validators.maxLength(50),
				Validators.pattern(/^[a-zA-Z\s\.\-']+$/)
			]),
			last_name: new FormControl('', [
				Validators.minLength(1), // Allows single letter last names if necessary, but 2 is safer. Let's stick to minLength(1) or 2. Actually 1 is better for last names.
				Validators.maxLength(50),
				Validators.pattern(/^[a-zA-Z\s\.\-']+$/)
			]),
			phone_num: new FormControl('', [Validators.required, Validators.pattern(/^[6-9]\d{9}$/)]),
			email: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(255)]),
			// Only presence/length here: the value must be picked from the colleges
			// list or added via the modal (enforced in onSignUp), and real college
			// names can contain digits and brackets.
			college_name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
			course: new FormControl('', [Validators.required]),
			year_of_passing: new FormControl(new Date().getFullYear(), [Validators.required]),
			city: new FormControl('', [
				Validators.minLength(2),
				Validators.maxLength(50),
				Validators.pattern(/^[a-zA-Z\s]+$/)
			]),
			password: new FormControl('', [
				Validators.required,
				Validators.minLength(8),
				Validators.maxLength(32),
				passwordStrengthValidator
			]),
			confirm_password: new FormControl('', [
				Validators.required
			])
		}, { validators: passwordCrossFieldValidator });
	}

	ngOnInit(): void {
		this.route.queryParams.subscribe(params => {
			this.redirectUrl = params['redirectUrl'] || '';
			if (params['phone']) {
				this.signUpForm.patchValue({ phone_num: params['phone'] });
			}
		});
	}

	get collegeNameControl(): FormControl {
		return this.signUpForm.get('college_name') as FormControl;
	}

	selectInstitution(institution: CollegeItem) {
		this.selectedInstitutionId = String(institution.id);
		if (institution.city) {
			this.signUpForm.patchValue({ city: institution.city }, { emitEvent: false });
		}
	}

	onCollegeSelectionCleared() {
		this.selectedInstitutionId = null;
	}

	onCantFindCollege() {
		const firstNameControl = this.signUpForm.get('first_name');
		const emailControl = this.signUpForm.get('email');
		firstNameControl?.markAsTouched();
		emailControl?.markAsTouched();

		if (firstNameControl?.invalid || emailControl?.invalid) {
			this._toastr.error('Please enter your first name and email before adding a new college.');
			return;
		}

		const dialogRef = this._dialog.open(CantFindCollegeModalComponent, {
			data: {
				firstName: this.signUpForm.value.first_name,
				lastName: this.signUpForm.value.last_name,
				email: this.signUpForm.value.email
			}
		});

		dialogRef.afterClosed().subscribe((college: CollegeItem | undefined) => {
			if (!college) return;

			this.selectedInstitutionId = String(college.id);
			this.signUpForm.patchValue({
				college_name: college.name,
				city: college.city || this.signUpForm.value.city
			}, { emitEvent: false });
		});
	}

	async onSignUp() {
		if (!this.signUpForm.valid) return;

		if (!this.selectedInstitutionId) {
			this.signUpForm.get('college_name')?.markAsTouched();
			this._toastr.error('Please select your college from the list, or use "Can\'t find your college?" to add it.');
			return;
		}

		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
		const language = navigator.language || '';
		const country = language.includes('-') ? language.split('-')[1] : 'Unknown';
		const playerId = await this._oneSignalService.getPlayerId() || '';

		const formValues = this.signUpForm.value;
		const payload: newSignUpPaylod = {
			first_name: formValues.first_name,
			last_name: formValues.last_name,
			phone_num: formValues.phone_num,
			phone_email: formValues.email,
			college_name: formValues.college_name,
			institution_id: this.selectedInstitutionId || undefined,
			course: formValues.course,
			year_of_passing: formValues.year_of_passing ? Number(formValues.year_of_passing) : undefined,
			city: formValues.city,
			is_default: true,
			registration_source: 'web',
			user_timezone: timeZone,
			country: country,
			onesignal_player_id: playerId,
			password: formValues.password
		};
		this.isLoading = true;
		this.subscription.add(
			this._authService.postNewSignUp(payload).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success(response.Message);
						this.router.navigate(['/mobile-otp'], {
							queryParams: {
								first_name: this.signUpForm.value.first_name,
								last_name: this.signUpForm.value.last_name,
								phone: this.signUpForm.value.phone_num,
								email: this.signUpForm.value.email,
								college_name: this.signUpForm.value.college_name,
								city: this.signUpForm.value.city,
								redirectUrl: this.redirectUrl
							}
						});
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => { this.isLoading = false; }),
				catchError(() => {
					this._toastr.error('Something went wrong. Please try again.');
					return of(null);
				})
			).subscribe()
		);
	}

	onPhoneInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const rawValue = input.value;
		const control = this.signUpForm.get('phone_num');
		if (!control) return;
		control.markAsTouched();
		const hasNonNumeric = /[^0-9]/.test(rawValue);
		const digitsOnly = rawValue.replace(/\D/g, '').slice(0, 10);
		input.value = digitsOnly;
		control.setValue(digitsOnly, { emitEvent: false });
		if (hasNonNumeric) {
			control.setErrors({ ...control.errors, nonNumeric: true });
		} else {
			const errors = { ...control.errors };
			delete errors['nonNumeric'];
			control.setErrors(Object.keys(errors).length ? errors : null);
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
